import { computed, toValue, type MaybeRefOrGetter } from 'vue'
// Imported rather than named as a string: `:is="'NuxtLink'"` doesn't resolve
// through Nuxt's compile-time component registration and renders a literal
// <NuxtLink> element instead of an anchor.
import { NuxtLink } from '#components'

// A scheme (`https:`, `mailto:`) or a protocol-relative `//` leaves the app.
const EXTERNAL = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i
// Of those, web pages open in a new tab; mailto:/tel: hand off in place.
const NEW_TAB = /^(?:https?:)?\/\//i

/**
 * The element an action should render as: a <button> with no `href`, an
 * external <a> (new tab, `rel="noopener"`) for another site, or a <NuxtLink>
 * for an in-app route so it doesn't reload the app.
 *
 * `noopener` rather than NuxtLink's default `noopener noreferrer`: outbound
 * links to Prefetch are attributed on the other side, and dropping the
 * referrer would hide where the visit came from.
 */
export function usePolymorphicLink(
  href: MaybeRefOrGetter<string | undefined>,
  type: MaybeRefOrGetter<string> = 'button',
) {
  const tag = computed(() => {
    const to = toValue(href)
    if (!to) return 'button'
    return EXTERNAL.test(to) ? 'a' : NuxtLink
  })

  const attrs = computed(() => {
    const to = toValue(href)
    if (!to) return { type: toValue(type) }
    if (!EXTERNAL.test(to)) return { to }
    return NEW_TAB.test(to) ? { href: to, target: '_blank', rel: 'noopener' } : { href: to }
  })

  return { tag, attrs }
}
